import React from "react";
import { api, unwrap } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, TrendingDown, Users, Wallet, ClipboardCheck, ShieldAlert } from "lucide-react";
import { formatMoney } from "@/lib/utils";
import { useI18n } from "@/i18n";

export default function Analytics() {
  const { t } = useI18n();
  const { data: kpi } = useQuery({ queryKey: ["kpis"], queryFn: async () => unwrap(await api.get("/analytics/kpis")) });
  const { data: risk } = useQuery({ queryKey: ["dropout-risk"], queryFn: async () => unwrap(await api.get("/analytics/dropout-risk")) });

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="analytics-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">{t("management.analyticsTitle")}</h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">{t("management.analyticsSubtitle")}</p>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-5 gap-3 mb-6">
        <KPI label="Active students" value={kpi?.students_active || 0} icon={<Users className="h-4 w-4" />} />
        <KPI label="Active staff" value={kpi?.staff_active || 0} icon={<Users className="h-4 w-4" />} />
        <KPI label="Overdue invoices" value={kpi?.overdue_invoices || 0} icon={<Wallet className="h-4 w-4 text-[var(--warning)]" />} accent="warning" />
        <KPI label="Attendance marks (7d)" value={kpi?.attendance_marks_7d || 0} icon={<ClipboardCheck className="h-4 w-4" />} />
        <KPI label="Open panic alerts" value={kpi?.open_panic_alerts || 0} icon={<ShieldAlert className="h-4 w-4 text-[var(--error)]" />} accent={kpi?.open_panic_alerts ? "error" : "info"} />
      </div>

      <div className="grid md:grid-cols-3 gap-3 mb-4">
        <RiskCard label="High risk" count={risk?.summary?.high || 0} accent="error" />
        <RiskCard label="Medium risk" count={risk?.summary?.medium || 0} accent="warning" />
        <RiskCard label="Low risk" count={risk?.summary?.low || 0} accent="success" />
      </div>

      <Card className="border-[var(--subtle)]">
        <CardHeader><CardTitle className="text-sm font-display flex items-center gap-2"><TrendingDown className="h-4 w-4 text-[var(--error)]" /> Dropout risk — Top 30 flagged students</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto"><table className="w-full text-sm grid-table">
            <thead><tr>
              <th className="text-left px-3 py-3">Student</th>
              <th className="text-left px-3 py-3">Admission</th>
              <th className="text-center px-3 py-3">Score</th>
              <th className="text-center px-3 py-3">Level</th>
              <th className="text-right px-3 py-3">Attendance (30d)</th>
              <th className="text-right px-3 py-3">Overdue</th>
              <th className="text-right px-3 py-3">Discipline pts</th>
            </tr></thead>
            <tbody>
              {(risk?.students || []).slice(0, 30).map((s) => (
                <tr key={s.student_id} data-testid={`risk-${s.student_id}`}>
                  <td className="px-3 py-3 font-medium">{s.name}</td>
                  <td className="px-3 py-3 font-mono-num text-xs text-[var(--text-muted)]">{s.admission_no}</td>
                  <td className="px-3 py-3 text-center font-mono-num font-bold">{s.risk_score}</td>
                  <td className="px-3 py-3 text-center">
                    <Badge className={
                      s.risk_level === "high" ? "bg-[var(--error-bg)] text-[var(--error)] border-0" :
                      s.risk_level === "medium" ? "bg-[var(--warning-bg)] text-[var(--warning)] border-0" :
                      "bg-[var(--success-bg)] text-[var(--success)] border-0"
                    }>{s.risk_level}</Badge>
                  </td>
                  <td className="px-3 py-3 text-right font-mono-num">{s.signals.attendance_pct_30d}%</td>
                  <td className="px-3 py-3 text-right font-mono-num">{s.signals.overdue_invoices > 0 ? `${s.signals.overdue_invoices} · ${formatMoney(s.signals.overdue_amount_paise)}` : "—"}</td>
                  <td className={`px-3 py-3 text-right font-mono-num ${s.signals.discipline_points < 0 ? "text-[var(--error)]" : ""}`}>{s.signals.discipline_points || 0}</td>
                </tr>
              ))}
              {(risk?.students || []).length === 0 && <tr><td colSpan={7} className="py-8 text-center text-[var(--text-muted)]">No risk data yet.</td></tr>}
            </tbody>
          </table></div>
        </CardContent>
      </Card>
    </div>
  );
}

function KPI({ label, value, icon, accent }) {
  const border = accent === "error" ? "border-l-4 border-l-[var(--error)]" : accent === "warning" ? "border-l-4 border-l-[var(--warning)]" : "";
  return (
    <Card className={`border-[var(--subtle)] ${border}`}>
      <CardContent className="p-4 flex items-center justify-between">
        <div>
          <div className="text-xs text-[var(--text-muted)] uppercase tracking-wider">{label}</div>
          <div className="text-2xl font-display font-bold mt-1 font-mono-num">{value}</div>
        </div>
        <div className="opacity-70">{icon}</div>
      </CardContent>
    </Card>
  );
}
function RiskCard({ label, count, accent }) {
  const borderMap = { error: "border-l-[var(--error)]", warning: "border-l-[var(--warning)]", success: "border-l-[var(--success)]", info: "border-l-[var(--info)]" };
  const textMap = { error: "text-[var(--error)]", warning: "text-[var(--warning)]", success: "text-[var(--success)]", info: "text-[var(--info)]" };
  return (
    <Card className={`border-[var(--subtle)] border-l-4 ${borderMap[accent] || borderMap.info}`}>
      <CardContent className="p-4 flex items-center justify-between">
        <div>
          <div className="text-xs text-[var(--text-muted)] uppercase tracking-wider">{label}</div>
          <div className="text-3xl font-display font-bold mt-1 font-mono-num">{count}</div>
        </div>
        <AlertTriangle className={`h-6 w-6 ${textMap[accent] || textMap.info}`} />
      </CardContent>
    </Card>
  );
}
